import React, { useState, useMemo } from 'react';

const CATEGORY_ICONS = {
  'General': '👕', 'Tops': '👕', 'Shirts': '👔', 'Bottoms': '👖', 'Pants': '👖',
  'Dresses': '👗', 'Footwear': '👟', 'Shoes': '👟', 'Headwear': '🎩', 'Hats': '🧢',
  'Accessories': '💍', 'Outerwear': '🧥', 'Jackets': '🧥', 'Traditional': '🕌',
  'Sportswear': '🏃', 'Formal': '🎩', 'Casual': '👕',
};

const DARIJA_COLORS = {
  red:    { darija: 'hmar',     arabic: 'حمر',    hex: '#EF4444' },
  blue:   { darija: 'zraq',     arabic: 'زرق',    hex: '#3B82F6' },
  green:  { darija: 'khdar',    arabic: 'خضر',    hex: '#22C55E' },
  yellow: { darija: 'sfar',     arabic: 'صفر',    hex: '#EAB308' },
  white:  { darija: 'byad',     arabic: 'بيض',    hex: '#F9FAFB' },
  black:  { darija: 'khal',     arabic: 'كحل',    hex: '#1F2937' },
  brown:  { darija: 'qahwi',    arabic: 'قهوي',   hex: '#92400E' },
  orange: { darija: 'limoni',   arabic: 'ليموني',  hex: '#F97316' },
  pink:   { darija: 'wrdi',     arabic: 'وردي',   hex: '#EC4899' },
  purple: { darija: 'bnafsjii', arabic: 'بنفسجي', hex: '#A855F7' },
  gray:   { darija: 'rmadi',    arabic: 'رمادي',   hex: '#6B7280' },
  beige:  { darija: 'beige',    arabic: 'بيج',    hex: '#D2B48C' },
};

const SIZE_DARIJA = {
  'XS': 'sghir bzzaf', 'S': 'sghir', 'M': 'wsat',
  'L': 'kbir', 'XL': 'kbir bzzaf', 'XXL': 'kbir kbir',
};

function MerchantBubble({ merchant, text, translation }) {
  return (
    <div className="flex items-start gap-3 mb-5">
      <div className="w-14 h-14 rounded-full bg-gradient-to-br from-amber-100 to-amber-50 border-2 border-amber-300 flex items-center justify-center text-2xl flex-shrink-0 shadow-sm">
        {merchant.image_url?.startsWith('http') ? (
          <img src={merchant.image_url} alt="" className="w-full h-full rounded-full object-cover" />
        ) : (merchant.image_url || '🧔')}
      </div>
      <div className="bg-[#F5F0E8] rounded-2xl rounded-tl-sm px-4 py-3 flex-1 shadow-sm">
        <p className="text-sm text-gray-800 font-medium">"{text}"</p>
        {translation && <p className="text-[11px] text-gray-400 mt-1">{translation}</p>}
      </div>
    </div>
  );
}

const isRealImage = (url) => url && (url.startsWith('http') || url.startsWith('/') || url.startsWith('data:'));

function ColorVocabCard({ color, darijaColors }) {
  const info = darijaColors[color?.toLowerCase()];
  if (!info) return null;
  return (
    <div className="bg-gradient-to-br from-[#2D6A4F]/10 to-[#40916C]/10 rounded-xl border border-[#2D6A4F]/20 p-4 mb-4">
      <p className="text-[10px] font-bold text-[#2D6A4F] uppercase tracking-wider mb-2">Color Learned</p>
      <div className="flex items-center justify-center gap-3">
        <div className="w-10 h-10 rounded-full border-2 border-gray-200 flex-shrink-0" style={{ backgroundColor: info.hex }} />
        <span className="text-lg text-gray-400">=</span>
        <div className="text-left">
          <p className="text-lg font-bold text-[#2D6A4F]">"{info.darija}"</p>
          {info.arabic && <p className="text-sm text-gray-500" dir="rtl">{info.arabic}</p>}
        </div>
      </div>
      <p className="text-[11px] text-gray-500 mt-2 capitalize">{color} in Darija</p>
    </div>
  );
}

function ItemCard({ item, isOwned, coins, isWishlisted, onToggleWishlist, onClick }) {
  const img = item.image_url;
  const isEmoji = !isRealImage(img);
  const canAfford = coins >= (item.base_price_tokens || 0);
  const needMore = !canAfford ? (item.base_price_tokens || 0) - coins : 0;

  return (
    <button
      onClick={onClick}
      className={`bg-white rounded-xl border-2 p-3 text-left transition-all hover:shadow-md hover:scale-[1.02] active:scale-[0.98] relative ${
        isOwned ? 'border-green-200 opacity-60'
        : !canAfford ? 'border-red-100 opacity-75'
        : 'border-gray-100 hover:border-[#D4A843]'
      }`}
    >
      {/* Wishlist heart */}
      {onToggleWishlist && !isOwned && (
        <span
          onClick={(e) => { e.stopPropagation(); onToggleWishlist(item.id); }}
          className={`absolute top-1.5 right-1.5 text-sm cursor-pointer transition-transform hover:scale-125 ${isWishlisted ? 'opacity-100' : 'opacity-30 hover:opacity-60'}`}
        >
          {isWishlisted ? '❤️' : '🤍'}
        </span>
      )}
      <div className="flex flex-col items-center gap-2">
        {isEmoji ? (
          <span className="text-4xl">{img || '👕'}</span>
        ) : (
          <img src={img} alt="" className="w-14 h-14 rounded-lg object-cover" />
        )}
        <div className="text-center">
          <p className="text-xs font-bold text-gray-800 leading-tight">{item.name_english}</p>
          {item.name_darija && (
            <p className="text-[10px] text-[#2D6A4F] font-medium">{item.name_darija}</p>
          )}
          <p className={`text-[10px] font-semibold mt-1 ${canAfford ? 'text-amber-600' : 'text-red-500'}`}>
            🪙 {item.base_price_tokens}
          </p>
        </div>
        {isOwned && (
          <span className="text-[9px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-bold">OWNED</span>
        )}
        {item.is_rare && !isOwned && canAfford && (
          <span className="text-[9px] bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-bold">RARE</span>
        )}
        {!isOwned && !canAfford && (
          <span className="text-[8px] text-red-400 font-semibold">Need 🪙{needMore} more</span>
        )}
      </div>
    </button>
  );
}

export default function FreeMarketShopping({
  merchant, items, ownedItems = [], coins = 0, gameSettings = {},
  wishlist = [], onBargain, onPurchase, onAddToCart, onToggleWishlist,
  onOpenCloset, onLearnWord, onBack, onExitGame, playSound
}) {
  const [step, setStep] = useState('greeting');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [purchasedItem, setPurchasedItem] = useState(null);
  const [purchasePrice, setPurchasePrice] = useState(null);

  const [showGreetingLesson, setShowGreetingLesson] = useState(() => {
    // Show once per merchant
    try {
      const seen = JSON.parse(localStorage.getItem('souk_greeting_seen') || '{}');
      return !seen[merchant?.id] && (merchant?.greeting_breakdown?.length > 0);
    } catch { return false; }
  });

  // Color discovery state
  const [colorDiscoveryPhase, setColorDiscoveryPhase] = useState(null); // 'asking' | 'has_it' | 'no_has'
  const [requestedColor, setRequestedColor] = useState(null);

  // Darija color map (admin-configurable with defaults)
  const darijaColors = gameSettings?.fm_color_translations || DARIJA_COLORS;

  // Size translations (admin-configurable with defaults)
  const sizeDarija = gameSettings?.fm_size_translations || SIZE_DARIJA;

  // Helper: get text from settings with fallback, replace {placeholder}
  const t = (key, fallback, replacements = {}) => {
    let text = gameSettings?.[key] || fallback;
    Object.entries(replacements).forEach(([k, v]) => { text = text.replace(`{${k}}`, v || ''); });
    return text;
  };

  // Get this merchant's items
  const merchantItems = useMemo(() =>
    items.filter(i => i.merchant_id === merchant.id && i.is_active !== false),
    [items, merchant.id]
  );

  // Get unique categories for this merchant
  const categories = useMemo(() => {
    const cats = {};
    merchantItems.forEach(item => {
      const cat = item.category || item.subcategory || 'Other';
      if (!cats[cat]) cats[cat] = 0;
      cats[cat]++;
    });
    return Object.entries(cats).map(([name, count]) => ({ name, count }));
  }, [merchantItems]);

  // Items filtered by selected category
  const categoryItems = useMemo(() => {
    if (!selectedCategory) return [];
    return merchantItems.filter(i => (i.category || i.subcategory || 'Other') === selectedCategory);
  }, [merchantItems, selectedCategory]);

  const isOwned = (itemId) => ownedItems.includes(itemId);

  const handleCategoryPick = (catName) => {
    setSelectedCategory(catName);
    const catItems = merchantItems.filter(i => (i.category || i.subcategory || 'Other') === catName);
    if (catItems.length === 0) {
      setStep('no-items');
    } else {
      setStep('item-select');
    }
    playSound?.('tick');
  };

  // NEW: Size FIRST, then color conversation (always shows for Darija learning)
  const handleItemPick = (item) => {
    setSelectedItem(item);
    setSelectedColor(null);
    setSelectedSize(null);
    setRequestedColor(null);
    setColorDiscoveryPhase(null);
    if (item.sizes_available?.length > 0) {
      setStep('size-select');
    } else {
      // Always show color conversation for Darija learning
      setStep('color-ask');
      setColorDiscoveryPhase('asking');
    }
    playSound?.('tick');
  };

  // After picking size → always go to color discovery
  const handleSizePick = (size) => {
    setSelectedSize(size);
    if (sizeDarija[size]) onLearnWord?.(sizeDarija[size], size, '', 'size');
    setStep('color-ask');
    setColorDiscoveryPhase('asking');
    playSound?.('tick');
  };

  // User picks a color to LEARN — check if merchant has it (learning only, doesn't change purchase)
  const handleColorRequest = (colorName) => {
    setRequestedColor(colorName);
    const available = (selectedItem?.colors_available || []).map(c => c.toLowerCase());
    // If item has no specific colors set, merchant "has everything"
    const hasColor = available.length === 0 || available.includes(colorName.toLowerCase());
    setColorDiscoveryPhase(hasColor ? 'has_it' : 'no_has');
    // Log the color word to vocab
    const dc = darijaColors[colorName.toLowerCase()];
    if (dc) onLearnWord?.(dc.darija, colorName, dc.arabic || '', 'color');
    playSound?.('tick');
  };

  const handleBuyDirect = () => {
    if (!selectedItem) return;
    const price = selectedItem.base_price_tokens || 0;
    if (price > coins) return;
    onPurchase?.(selectedItem, price);
    setPurchasedItem(selectedItem);
    setPurchasePrice(price);
    if (selectedItem.name_darija) onLearnWord?.(selectedItem.name_darija, selectedItem.name_english, '', 'clothing');
    setStep('purchase-result');
    playSound?.('coin');
  };

  const handleBargain = () => {
    if (!selectedItem) return;
    onBargain?.(selectedItem, merchant);
  };

  // Called by parent after bargain completes
  const handleAfterBargain = (item) => {
    setPurchasedItem(item);
    setPurchasePrice(item.finalPrice || item.cartPrice || item.base_price_tokens || 0);
    setStep('purchase-result');
  };

  const handleKeepShopping = () => {
    setSelectedItem(null);
    setSelectedColor(null);
    setSelectedSize(null);
    setPurchasedItem(null);
    setPurchasePrice(null);
    setRequestedColor(null);
    setColorDiscoveryPhase(null);
    setStep('greeting');
    playSound?.('tick');
  };

  const goBack = () => {
    if (step === 'item-select' || step === 'no-items') {
      setSelectedCategory(null);
      setStep('greeting');
    } else if (step === 'size-select') {
      // Size is first after item pick
      setStep('item-select');
    } else if (step === 'color-ask') {
      // Color is after size
      setRequestedColor(null);
      setColorDiscoveryPhase(null);
      setSelectedColor(null);
      if (selectedItem?.sizes_available?.length > 0) {
        setStep('size-select');
      } else {
        setStep('item-select');
      }
    } else if (step === 'ready-to-buy') {
      // Always go back to color-ask (it always shows now)
      setStep('color-ask');
      setColorDiscoveryPhase('asking');
      setRequestedColor(null);
      setSelectedColor(null);
    } else {
      onBack?.();
    }
    playSound?.('tick');
  };

  // Helper: get Darija name for a color
  const colorDarija = (color) => darijaColors[color?.toLowerCase()]?.darija || color;

  // Item preview card (reused in size-select and color-ask)
  const ItemPreview = () => (
    <div className="bg-white rounded-xl p-3 mb-4 flex items-center gap-3 border border-gray-100">
      {isRealImage(selectedItem.image_url) ? (
        <img src={selectedItem.image_url} alt="" className="w-10 h-10 rounded-lg object-cover" />
      ) : (
        <span className="text-3xl">{selectedItem.image_url || '👕'}</span>
      )}
      <div>
        <p className="text-sm font-bold text-gray-800">{selectedItem.name_english}</p>
        {selectedItem.name_darija && (
          <p className="text-[11px] text-[#2D6A4F]">{selectedItem.name_darija}</p>
        )}
        {selectedSize && (
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-[10px] bg-gray-100 px-1.5 py-0.5 rounded font-medium text-gray-600">
              {selectedSize} {sizeDarija[selectedSize] ? `(${sizeDarija[selectedSize]})` : ''}
            </span>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F5F0E8] animate-fade-up">
      {/* Top bar with breadcrumb */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-1 text-xs min-w-0 overflow-hidden">
            {onExitGame && (
              <button onClick={onExitGame} className="text-gray-400 hover:text-gray-700 transition-colors mr-0.5" title={gameSettings.breadcrumb_home || 'Home'}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0h4" />
                </svg>
              </button>
            )}
            <button onClick={onBack} className="text-gray-400 hover:text-gray-700 whitespace-nowrap transition-colors">{gameSettings.breadcrumb_souk || 'Souk'}</button>
            <span className="text-gray-300">›</span>
            {step === 'greeting' ? (
              <span className="text-gray-700 font-semibold truncate">{merchant.name || merchant.name_english}</span>
            ) : (
              <button onClick={() => { setSelectedCategory(null); setSelectedItem(null); setStep('greeting'); }} className="text-gray-400 hover:text-gray-700 whitespace-nowrap transition-colors">{merchant.name || merchant.name_english}</button>
            )}
            {selectedCategory && step !== 'greeting' && (
              <>
                <span className="text-gray-300">›</span>
                {step === 'item-select' ? (
                  <span className="text-gray-700 font-semibold truncate">{selectedCategory}</span>
                ) : (
                  <button onClick={() => { setSelectedItem(null); setStep('item-select'); }} className="text-gray-400 hover:text-gray-700 whitespace-nowrap transition-colors">{selectedCategory}</button>
                )}
              </>
            )}
            {selectedItem && !['greeting', 'item-select', 'no-items'].includes(step) && (
              <>
                <span className="text-gray-300">›</span>
                <span className="text-gray-700 font-semibold truncate">{selectedItem.name_english}</span>
              </>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 ml-2">
            <span className="text-sm font-bold text-amber-700">🪙 {coins}</span>
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-5">

        {/* Greeting micro-lesson popup (first visit to merchant with greeting_breakdown data) */}
        {showGreetingLesson && merchant.greeting_breakdown?.length > 0 && (
          <div className="mb-4 bg-gradient-to-br from-[#2D6A4F]/10 to-[#40916C]/10 rounded-2xl border border-[#2D6A4F]/20 p-4 animate-fade-up">
            <p className="text-[10px] font-bold text-[#2D6A4F] uppercase tracking-wider mb-2">{gameSettings.greeting_lesson_title || 'Darija Lesson'} 📖</p>
            <p className="text-sm font-bold text-gray-800 mb-2">"{merchant.greeting_darija}"</p>
            <div className="flex flex-wrap gap-1.5 mb-3">
              {merchant.greeting_breakdown.map((w, i) => (
                <div key={i} className="bg-white rounded-lg px-2.5 py-1.5 border border-[#2D6A4F]/20">
                  <p className="text-xs font-bold text-[#2D6A4F]">{w.darija}</p>
                  <p className="text-[10px] text-gray-500">{w.english}</p>
                </div>
              ))}
            </div>
            <button
              onClick={() => {
                setShowGreetingLesson(false);
                try {
                  const seen = JSON.parse(localStorage.getItem('souk_greeting_seen') || '{}');
                  seen[merchant.id] = true;
                  localStorage.setItem('souk_greeting_seen', JSON.stringify(seen));
                } catch {}
                // Log greeting words to vocab
                merchant.greeting_breakdown.forEach(w => onLearnWord?.(w.darija, w.english, '', 'greeting'));
              }}
              className="w-full py-2 rounded-xl bg-[#2D6A4F] hover:bg-[#40916C] text-white font-bold text-xs transition-colors"
            >
              {gameSettings.greeting_lesson_button || 'Got it! 👍'}
            </button>
          </div>
        )}

        {/* STEP: GREETING — merchant welcomes, show category buttons */}
        {step === 'greeting' && (
          <div className="animate-fade-up">
            <MerchantBubble
              merchant={merchant}
              text={merchant.greeting_darija || t('fm_greeting_darija', 'Marhba! Achno bghiti?')}
              translation={merchant.greeting_english || t('fm_greeting_english', 'Welcome! What do you want?')}
            />

            {/* Category buttons */}
            <p className="text-xs text-gray-500 font-semibold mb-3 ml-1">{t('fm_category_prompt_english', 'What are you looking for?')}</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
              {categories.map(cat => (
                <button
                  key={cat.name}
                  onClick={() => handleCategoryPick(cat.name)}
                  className="bg-white rounded-xl border-2 border-gray-100 hover:border-[#D4A843] p-4 text-center transition-all hover:shadow-md active:scale-[0.97]"
                >
                  <span className="text-2xl block mb-1">{CATEGORY_ICONS[cat.name] || '🏷️'}</span>
                  <p className="text-sm font-bold text-gray-800">{cat.name}</p>
                  <p className="text-[10px] text-gray-400">{cat.count} items</p>
                </button>
              ))}
            </div>

            {/* Bottom actions */}
            <div className="flex gap-2.5 mt-5">
              <button
                onClick={onOpenCloset}
                className="flex-1 py-2.5 rounded-xl border-2 border-[#2D6A4F] text-[#2D6A4F] font-bold text-sm hover:bg-[#2D6A4F] hover:text-white transition-colors"
              >
                👗 {t('fm_closet_button', 'My Closet')}
              </button>
              <button
                onClick={onBack}
                className="px-5 py-2.5 rounded-xl border-2 border-gray-200 text-gray-500 font-semibold text-sm hover:border-gray-300 transition-colors"
              >
                {t('fm_back_to_souk_button', '← Back to Souk')}
              </button>
            </div>
          </div>
        )}

        {/* STEP: NO ITEMS in this category */}
        {step === 'no-items' && (
          <div className="animate-fade-up">
            <MerchantBubble
              merchant={merchant}
              text={t('fm_no_items_darija', 'Smahli, ma3ndish {category}... Shuf shi 7aja okhra!', { category: selectedCategory })}
              translation={t('fm_no_items_english', "Sorry, I don't have {category}... Try something else!", { category: selectedCategory })}
            />
            <button
              onClick={() => { setSelectedCategory(null); setStep('greeting'); }}
              className="w-full py-3 rounded-xl bg-[#C84B31] text-white font-bold text-sm hover:bg-[#a33d27] transition-colors"
            >
              {t('fm_back_to_categories', '← See Other Categories')}
            </button>
          </div>
        )}

        {/* STEP: ITEM SELECT — show items in category */}
        {step === 'item-select' && (
          <div className="animate-fade-up">
            <MerchantBubble
              merchant={merchant}
              text={t('fm_show_items_darija', '3ndi {category} zwinin! Ashmen wa7ed 3jbek?', { category: selectedCategory })}
              translation={t('fm_show_items_english', 'I have nice {category}! Which one do you like?', { category: selectedCategory })}
            />

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
              {categoryItems.map(item => (
                <ItemCard
                  key={item.id}
                  item={item}
                  isOwned={isOwned(item.id)}
                  coins={coins}
                  isWishlisted={wishlist.includes(item.id)}
                  onToggleWishlist={onToggleWishlist}
                  onClick={() => handleItemPick(item)}
                />
              ))}
            </div>

            <button
              onClick={goBack}
              className="w-full mt-4 py-2.5 rounded-xl border-2 border-gray-200 text-gray-500 font-semibold text-sm hover:border-gray-300 transition-colors"
            >
              ← Back to Categories
            </button>
          </div>
        )}

        {/* STEP: SIZE SELECT (now FIRST, before colors) */}
        {step === 'size-select' && selectedItem && (
          <div className="animate-fade-up">
            <MerchantBubble
              merchant={merchant}
              text={t('fm_ask_size_darija', 'Achmen qiyas bghiti?')}
              translation={t('fm_ask_size_english', 'What size do you want?')}
            />

            <ItemPreview />

            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5">
              {selectedItem.sizes_available.map(size => (
                <button
                  key={size}
                  onClick={() => handleSizePick(size)}
                  className="bg-white rounded-xl border-2 border-gray-100 hover:border-[#D4A843] py-3 text-center transition-all hover:shadow-md active:scale-[0.97]"
                >
                  <p className="text-lg font-bold text-gray-800">{size}</p>
                  {sizeDarija[size] && (
                    <p className="text-[10px] text-[#2D6A4F] font-medium mt-0.5">{sizeDarija[size]}</p>
                  )}
                </button>
              ))}
            </div>

            <button
              onClick={goBack}
              className="w-full mt-4 py-2.5 rounded-xl border-2 border-gray-200 text-gray-500 font-semibold text-sm hover:border-gray-300 transition-colors"
            >
              ← Back
            </button>
          </div>
        )}

        {/* STEP: COLOR ASK — friendly merchant offers different color (learning only) */}
        {step === 'color-ask' && selectedItem && (
          <div className="animate-fade-up">

            {/* Phase: ASKING — merchant is friendly, offers to show different colors */}
            {colorDiscoveryPhase === 'asking' && (
              <>
                <MerchantBubble
                  merchant={merchant}
                  text={t('fm_color_ask_preference_darija', 'Ikhtiyar zwin! Bghiti tjarreb lon akhor?')}
                  translation={t('fm_color_ask_preference_english', 'Nice choice! Want to try a different color?')}
                />

                <ItemPreview />

                <p className="text-xs text-gray-500 font-semibold mb-2 ml-1">{t('fm_color_pick_label', 'Pick a color to learn — Khtar lon:')}</p>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {Object.entries(darijaColors).map(([colorName, info]) => (
                    <button
                      key={colorName}
                      onClick={() => handleColorRequest(colorName)}
                      className="bg-white rounded-xl border-2 border-gray-100 hover:border-[#D4A843] p-2.5 text-center transition-all hover:shadow-md active:scale-[0.97]"
                    >
                      <div
                        className="w-8 h-8 rounded-full mx-auto mb-1.5 border-2 border-gray-200"
                        style={{ backgroundColor: info.hex }}
                      />
                      <p className="text-[11px] font-semibold text-gray-700 capitalize">{colorName}</p>
                      <p className="text-[9px] text-[#2D6A4F] font-medium">{info.darija}</p>
                    </button>
                  ))}
                </div>

                {/* No thanks — keep original */}
                <button
                  onClick={() => setStep('ready-to-buy')}
                  className="w-full mt-3 py-2.5 rounded-xl border-2 border-gray-200 text-gray-500 font-semibold text-sm hover:border-gray-300 transition-colors"
                >
                  {t('fm_color_no_thanks', 'No thanks, I like this one')}
                </button>
                <button
                  onClick={goBack}
                  className="w-full mt-2 py-2 rounded-xl text-gray-400 font-semibold text-sm hover:text-gray-600 transition-colors"
                >
                  ← Back
                </button>
              </>
            )}

            {/* Phase: HAS IT — merchant has the color, shows vocab card */}
            {colorDiscoveryPhase === 'has_it' && (
              <>
                <MerchantBubble
                  merchant={merchant}
                  text={t('fm_color_has_it_darija', '3endi lon {color}! Hahowa!', { color: colorDarija(requestedColor) })}
                  translation={t('fm_color_has_it_english', 'I have {color}! Here it is!', { color: requestedColor })}
                />

                <ColorVocabCard color={requestedColor} darijaColors={darijaColors} />

                <ItemPreview />

                <button
                  onClick={() => setStep('ready-to-buy')}
                  className="w-full py-3.5 rounded-xl bg-[#2D6A4F] hover:bg-[#40916C] text-white font-bold text-sm transition-colors shadow-md"
                >
                  {t('fm_color_continue', 'Continue to buy {item}', {item: selectedItem.name_english})}
                </button>
                <button
                  onClick={() => { setColorDiscoveryPhase('asking'); setRequestedColor(null); }}
                  className="w-full mt-2 py-2 rounded-xl text-gray-400 font-semibold text-sm hover:text-gray-600 transition-colors"
                >
                  {t('fm_color_try_another', '← Try another color')}
                </button>
              </>
            )}

            {/* Phase: NO HAS — merchant apologizes, encourages keeping original */}
            {colorDiscoveryPhase === 'no_has' && (
              <>
                <MerchantBubble
                  merchant={merchant}
                  text={t('fm_color_no_has_darija', 'Smahli, ma3ndish {color}. Walakin had {item} li khtarti zwina!', { color: colorDarija(requestedColor), item: selectedItem.name_darija || selectedItem.name_english })}
                  translation={t('fm_color_no_has_english', "Sorry, I don't have {color}. But the {item} you picked is great!", { color: requestedColor, item: selectedItem.name_english })}
                />

                <ColorVocabCard color={requestedColor} darijaColors={darijaColors} />

                <ItemPreview />

                <button
                  onClick={() => setStep('ready-to-buy')}
                  className="w-full py-3.5 rounded-xl bg-[#2D6A4F] hover:bg-[#40916C] text-white font-bold text-sm transition-colors shadow-md"
                >
                  {t('fm_color_keep_item', 'Keep my {item}', {item: selectedItem.name_english})}
                </button>
                <button
                  onClick={() => { setColorDiscoveryPhase('asking'); setRequestedColor(null); }}
                  className="w-full mt-2 py-2 rounded-xl text-gray-400 font-semibold text-sm hover:text-gray-600 transition-colors"
                >
                  {t('fm_color_try_another', '← Try another color')}
                </button>
              </>
            )}
          </div>
        )}

        {/* STEP: READY TO BUY — summary + bargain/buy buttons */}
        {step === 'ready-to-buy' && selectedItem && (
          <div className="animate-fade-up">
            <MerchantBubble
              merchant={merchant}
              text={t('fm_deal_darija', 'Hada {item}. Bghiti tsaweb?', { item: `${selectedItem.name_darija || selectedItem.name_english}${selectedSize ? `, qiyas ${selectedSize}` : ''}` })}
              translation={t('fm_deal_english', 'This {item}. Want to deal?', { item: `${selectedItem.name_english}${selectedSize ? `, size ${selectedSize}` : ''}` })}
            />

            {/* Item summary card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 mb-5">
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 rounded-xl bg-gray-50 flex items-center justify-center">
                  {isRealImage(selectedItem.image_url) ? (
                    <img src={selectedItem.image_url} alt="" className="w-16 h-16 rounded-lg object-cover" />
                  ) : (
                    <span className="text-5xl">{selectedItem.image_url || '👕'}</span>
                  )}
                </div>
                <div className="flex-1">
                  <p className="font-bold text-gray-900">{selectedItem.name_english}</p>
                  {selectedItem.name_darija && (
                    <p className="text-sm text-[#2D6A4F] font-medium">{selectedItem.name_darija}</p>
                  )}
                  {selectedItem.pronunciation && (
                    <p className="text-[10px] text-gray-400 italic">{selectedItem.pronunciation}</p>
                  )}
                  {selectedSize && (
                    <div className="flex items-center gap-3 mt-2">
                      <span className="text-xs bg-gray-100 px-2 py-0.5 rounded font-medium">
                        {selectedSize} {sizeDarija[selectedSize] ? `(${sizeDarija[selectedSize]})` : ''}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between">
                <span className="text-sm text-gray-500">{t('fm_full_price_label', 'Full price')}</span>
                <span className="text-lg font-bold text-amber-700">🪙 {selectedItem.base_price_tokens}</span>
              </div>
              {isOwned(selectedItem.id) && (
                <div className="mt-2 bg-green-50 rounded-lg p-2 text-center">
                  <span className="text-xs text-green-700 font-semibold">{t('fm_owned_warning', 'You already own this item')}</span>
                </div>
              )}
            </div>

            {/* Action buttons */}
            <div className="space-y-2.5">
              <button
                onClick={() => { onAddToCart?.(selectedItem); playSound?.('coin'); }}
                disabled={isOwned(selectedItem.id)}
                className={`w-full py-3.5 rounded-xl font-bold text-sm transition-all ${
                  isOwned(selectedItem.id)
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-teal-600 hover:bg-teal-700 text-white shadow-md'
                }`}
              >
                🛒 {t('fm_add_to_cart_button', 'Add to Cart')} — 🪙 {selectedItem.base_price_tokens}
              </button>
              <button
                onClick={handleBargain}
                disabled={isOwned(selectedItem.id)}
                className={`w-full py-3 rounded-xl font-bold text-sm transition-all ${
                  isOwned(selectedItem.id)
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-[#C84B31] hover:bg-[#a33d27] text-white shadow-md'
                }`}
              >
                {t('fm_bargain_button', 'Bargain! Saweb m3aya!')} 💬
              </button>
              <button
                onClick={handleBuyDirect}
                disabled={isOwned(selectedItem.id) || (selectedItem.base_price_tokens || 0) > coins}
                className={`w-full py-3 rounded-xl font-bold text-sm transition-all ${
                  isOwned(selectedItem.id) || (selectedItem.base_price_tokens || 0) > coins
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-[#2D6A4F] hover:bg-[#40916C] text-white'
                }`}
              >
                {t('fm_buy_button', 'Buy at full price')} 🪙 {selectedItem.base_price_tokens}
              </button>
              {(selectedItem.base_price_tokens || 0) > coins && !isOwned(selectedItem.id) && (
                <p className="text-center text-xs text-amber-600 font-medium">
                  {t('fm_need_tokens', 'Need 🪙 {X} more tokens').replace('{X}', (selectedItem.base_price_tokens || 0) - coins)}
                  {' — '}{t('fm_bargain_tip', 'Try bargaining for a lower price!')}
                </p>
              )}
            </div>

            <button
              onClick={goBack}
              className="w-full mt-3 py-2.5 rounded-xl border-2 border-gray-200 text-gray-500 font-semibold text-sm hover:border-gray-300 transition-colors"
            >
              ← Back
            </button>
          </div>
        )}

        {/* STEP: PURCHASE RESULT */}
        {step === 'purchase-result' && purchasedItem && (
          <div className="animate-fade-up text-center">
            <MerchantBubble
              merchant={merchant}
              text={t('fm_purchase_darija', 'Mabrouk! Hadi 7aja zwina 3lik!')}
              translation={t('fm_purchase_english', 'Congratulations! This looks great on you!')}
            />

            <div className="bg-white rounded-2xl shadow-md border border-green-100 p-6 mb-5">
              <div className="text-4xl mb-3">🎉</div>
              <p className="font-bold text-gray-900 text-lg">{t('fm_item_purchased_title', 'Item Purchased!')}</p>
              <div className="mt-3 flex items-center justify-center gap-3">
                {isRealImage(purchasedItem.image_url) ? (
                  <img src={purchasedItem.image_url} alt="" className="w-14 h-14 rounded-xl object-cover" />
                ) : (
                  <span className="text-4xl">{purchasedItem.image_url || '👕'}</span>
                )}
                <div className="text-left">
                  <p className="font-bold text-gray-800">{purchasedItem.name_english}</p>
                </div>
              </div>

              {/* Souk Receipt */}
              {purchasePrice != null && (
                <div className="mt-4 bg-amber-50 rounded-xl border border-amber-200 p-4 text-left">
                  <p className="text-xs font-bold text-amber-800 text-center mb-3">{gameSettings.receipt_title || 'Souk Receipt'} 🧾</p>
                  <div className="space-y-1.5 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">{gameSettings.receipt_merchant_label || 'Merchant'}</span>
                      <span className="font-medium text-gray-900">{merchant.name || merchant.name_english}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">{gameSettings.receipt_item_label || 'Item'}</span>
                      <span className="font-medium text-gray-900">{purchasedItem.name_darija || purchasedItem.name_english}</span>
                    </div>
                    {selectedSize && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">{gameSettings.receipt_size_label || 'Size'}</span>
                        <span className="font-medium text-gray-900">{selectedSize} {sizeDarija[selectedSize] ? `(${sizeDarija[selectedSize]})` : ''}</span>
                      </div>
                    )}
                    <div className="border-t border-amber-200 pt-1.5 flex justify-between">
                      <span className="text-gray-600">{gameSettings.receipt_paid_label || 'Paid'}</span>
                      <span className="font-bold text-amber-800">🪙 {purchasePrice}</span>
                    </div>
                    {purchasePrice < (purchasedItem.base_price_tokens || 0) && (
                      <div className="flex justify-between text-green-700">
                        <span>{gameSettings.receipt_saved_label || 'Saved'}</span>
                        <span className="font-bold">🪙 {(purchasedItem.base_price_tokens || 0) - purchasePrice} (-{Math.round(((purchasedItem.base_price_tokens - purchasePrice) / purchasedItem.base_price_tokens) * 100)}%)</span>
                      </div>
                    )}
                  </div>
                  <p className="text-center text-xs text-amber-700 mt-3 font-medium">{gameSettings.receipt_thanks || 'Shukran! — Thank you!'}</p>
                </div>
              )}

              {/* Darija Vocabulary Flash Card — Item name */}
              {purchasedItem.name_darija && (
                <div className="mt-4 bg-gradient-to-br from-[#2D6A4F]/10 to-[#40916C]/10 rounded-xl border border-[#2D6A4F]/20 p-4">
                  <p className="text-[10px] font-bold text-[#2D6A4F] uppercase tracking-wider mb-2">{t('fm_word_learned_label', 'Word Learned')}</p>
                  <div className="flex items-center justify-center gap-3">
                    <span className="text-2xl">{isRealImage(purchasedItem.image_url) ? '👕' : (purchasedItem.image_url || '👕')}</span>
                    <span className="text-lg text-gray-400">=</span>
                    <div className="text-left">
                      <p className="text-lg font-bold text-[#2D6A4F]">"{purchasedItem.name_darija}"</p>
                      {purchasedItem.pronunciation && (
                        <p className="text-xs text-gray-500 italic">/{purchasedItem.pronunciation}/</p>
                      )}
                    </div>
                  </div>
                  <p className="text-[11px] text-gray-500 mt-2">{purchasedItem.name_english} in Darija</p>
                </div>
              )}

              {/* Color Learned card (if user explored a color during shopping) */}
              {requestedColor && (
                <div className="mt-3">
                  <ColorVocabCard color={requestedColor} darijaColors={darijaColors} />
                </div>
              )}

              <p className="text-xs text-gray-400 mt-3">{t('fm_added_to_collection', 'Added to your collection')}</p>
            </div>

            <div className="space-y-2.5">
              <button
                onClick={handleKeepShopping}
                className="w-full py-3 rounded-xl bg-[#2D6A4F] hover:bg-[#40916C] text-white font-bold text-sm transition-colors"
              >
                {t('fm_keep_shopping', 'Keep Shopping with {merchant}', {merchant: merchant.name || merchant.name_english})}
              </button>
              <button
                onClick={onBack}
                className="w-full py-3 rounded-xl border-2 border-[#C84B31] text-[#C84B31] hover:bg-[#C84B31] hover:text-white font-bold text-sm transition-colors"
              >
                {t('fm_visit_another', 'Visit Another Merchant')}
              </button>
              <button
                onClick={onOpenCloset}
                className="w-full py-2.5 rounded-xl border-2 border-gray-200 text-gray-600 font-semibold text-sm hover:border-gray-300 transition-colors"
              >
                👗 {t('fm_open_closet', 'Open Closet')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
